import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Icons
import DescriptionIcon from "@mui/icons-material/Description";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const Dashboard = () => {
  const navigate = useNavigate();
  const [patientName, setPatientName] = useState("Patient");
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    const storedName = localStorage.getItem("neuro_patientportal_userName");
    if (storedName) setPatientName(storedName);

    const hour = new Date().getHours();
    if (hour < 12) setCurrentTime("Good Morning");
    else if (hour < 18) setCurrentTime("Good Afternoon");
    else setCurrentTime("Good Evening");
  }, []);

  // ✅ Only 2 Quick Actions now (My Forms + My Profile)
  const quickActions = [
    {
      title: "My Forms",
      description: "View & fill out medical forms",
      icon: <DescriptionIcon />,
      color: "#5651F4",
      bgColor: "#EEEDFE",
      route: "/dashboard/forms",
    },
    {
      title: "My Profile",
      description: "Personal information",
      icon: <PersonIcon />,
      color: "#EC4899",
      bgColor: "#FCE7F3",
      route: "/dashboard/patient-profile", // ✅ lowercase matches App.js
    },
  ];

  const healthMetrics = [
    {
      label: "Heart Rate",
      value: "72",
      unit: "bpm",
      icon: <MonitorHeartIcon />,
      color: "#EF4444",
      status: "Normal",
      progress: 72,
    },
    {
      label: "Blood Pressure",
      value: "120/80",
      unit: "mmHg",
      icon: <FavoriteIcon />,
      color: "#5651F4",
      status: "Normal",
      progress: 80,
    },
    {
      label: "Temperature",
      value: "98.6",
      unit: "°F",
      icon: <ThermostatIcon />,
      color: "#F59E0B",
      status: "Normal",
      progress: 65,
    },
    {
      label: "Blood Sugar",
      value: "95",
      unit: "mg/dL",
      icon: <BloodtypeIcon />,
      color: "#059669",
      status: "Normal",
      progress: 55,
    },
  ];

  const upcomingAppointments = [
    {
      doctor: "Dr. Sarah Johnson",
      specialty: "Cardiologist",
      date: "Nov 28, 2025",
      time: "10:30 AM",
      type: "Follow-up",
    },
    {
      doctor: "Dr. Michael Chen",
      specialty: "General Physician",
      date: "Dec 05, 2025",
      time: "02:00 PM",
      type: "Check-up",
    },
  ];

  const recentActivity = [
    {
      title: "Prescription Renewed",
      description: "Metformin 500mg - 30 days supply",
      time: "2 hours ago",
      icon: <MedicationIcon />,
      color: "#059669",
    },
    {
      title: "Lab Results Available",
      description: "Blood work completed",
      time: "1 day ago",
      icon: <CheckCircleIcon />,
      color: "#5651F4",
    },
    {
      title: "Appointment Confirmed",
      description: "Dr. Sarah Johnson - Nov 28",
      time: "2 days ago",
      icon: <EventAvailableIcon />,
      color: "#F59E0B",
    },
  ];

  return (
    <Box
      sx={{ p: { xs: 1, sm: 1, md: 2 }, bgcolor: "#F8FAFC", minHeight: "100vh", borderRadius:"10px " }}
      className="top-navbar"
    >
      {/* ═══════════════════════════════════════ */}
      {/* 👋 WELCOME HEADER                        */}
      {/* ═══════════════════════════════════════ */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          background:
          "linear-gradient( #252194 50%, #3F3DB8 100%)",
          color: "white",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 8px 32px rgba(86, 81, 244, 0.25)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(194, 12, 12, 0.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -80,
            right: 100,
            width: 250,
            height: 250,
            borderRadius: "50%",
            background: "rgba(194, 24, 24, 0.05)",
          }}
        />

        <CardContent
          sx={{ p: { xs: 3, sm: 4 }, position: "relative", zIndex: 1 }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              {/* ✅ FORCED WHITE - Good Afternoon */}
              <Typography
                sx={{
                  color: "#FFFFFF !important",
                  opacity: 1,
                  mb: 0.5,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 600,
                  ".dark-theme &": {
                    color: "#FFFFFF !important",
                  },
                }}
              >
                {currentTime}
              </Typography>

              {/* ✅ Welcome title (was h8 which is invalid, changed to h4) */}
              <Typography
                sx={{
                  color: "#FFFFFF !important",
                  fontWeight: 800,
                  mb: 1,
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" },
                  ".dark-theme &": {
                    color: "#FFFFFF !important",
                  },
                }}
              >
                Welcome back, {patientName}!
              </Typography>

              {/* ✅ FORCED WHITE - Description */}
              <Typography
                sx={{
                  color: "#FFFFFF !important",
                  opacity: 1,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  maxWidth: "500px",
                  fontWeight: 400,
                  ".dark-theme &": {
                    color: "#FFFFFF !important",
                  },
                }}
              >
                Manage your health records, appointments, and prescriptions all
                in one place.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ textAlign: { xs: "left", sm: "right" } }}
            >
              <Avatar
                sx={{
                  width: { xs: 70, sm: 90 },
                  height: { xs: 70, sm: 90 },
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                  fontSize: { xs: "1.75rem", sm: "2.25rem" },
                  fontWeight: 700,
                  display: "inline-flex",
                  color: "#FFFFFF !important",
                }}
              >
                {patientName.charAt(0).toUpperCase()}
              </Avatar>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════ */}
      {/* ⚡ QUICK ACTIONS (Only 2 cards, half width each) */}
      {/* ═══════════════════════════════════════ */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "#1F2937",
          mb: 2,
          mt: 3,
          fontSize: { xs: "1rem", sm: "1.125rem" },
          ".dark-theme &": {
            color: "#FFFFFF !important",
          },
        }}
      >
        Quick Actions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4, }} xs={12} sm={12}>
        {quickActions.map((action, index) => (
          // ✅ Half width on all screens (xs=6 stays for mobile, md=6 makes it half on desktop)
          <Grid item xs={12} sm={6} md={6} key={index} sx={{ width: "49%", height:"10rem" }}>
            <Card
              onClick={() => navigate(action.route)}
              elevation={0}
              sx={{
                backgroundColor:"#f8f8f8",
                borderRadius: 3,
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: "1px solid #9e9e9e",
                height: "100%",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0 8px 20px ${action.color}25`,
                  borderColor: action.color,
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: action.bgColor,
                    color: action.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  {action.icon}
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#1F2937",
                    mb: 0.5,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    ".dark-theme &": {
                      color: "#FFFFFF !important",
                    },
                  }}
                >
                  {action.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6B7280",
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    ".dark-theme &": {
                      color: "rgba(255,255,255,0.7) !important",
                    },
                  }}
                >
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

    </Box>
  );
};

export default Dashboard;